import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { getProductPreviewSrc } from "@/constants/catalog-ui";
import type { CatalogProduct } from "@/types/api";

const ROW_CLASS =
  "flex w-full items-center gap-3 rounded-lg p-2 text-left transition-colors hover:bg-secondary/70 focus-visible:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring";

function highlightMatch(text: string, query: string): ReactNode {
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (words.length === 0) return text;

  const pattern = words.map((w) => w.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).join("|");
  const regex = new RegExp(`(${pattern})`, "gi");
  const parts = text.split(regex);
  if (parts.length === 1) return text;

  const testRegex = new RegExp(`^(?:${pattern})$`, "i");
  return (
    <>
      {parts.map((part, i) =>
        testRegex.test(part) ? (
          <span key={i} className="bg-primary/20 text-primary font-semibold">
            {part}
          </span>
        ) : (
          part
        ),
      )}
    </>
  );
}

interface ProductResultRowProps {
  product: CatalogProduct;
  /** A link to this page; without it the row is a button. */
  to?: string;
  onClick: () => void;
  /** The search text, highlighted in the name and the article. */
  highlight?: string;
  /** Goes after the article, e.g. the stock. */
  detail?: ReactNode;
  /** The right edge: the price, or what a click does. */
  trailing?: ReactNode;
}

/** One product in a search result list: thumbnail, name, article. */
export function ProductResultRow({
  product,
  to,
  onClick,
  highlight = "",
  detail,
  trailing,
}: ProductResultRowProps) {
  // Spans only: the row may be a button, which takes no block content.
  const content = (
    <>
      <span className="size-12 shrink-0 overflow-hidden rounded-lg bg-secondary/70">
        {/* Decorative: the name is right next to it. */}
        <img
          src={getProductPreviewSrc(product)}
          alt=""
          loading="lazy"
          className="h-full w-full object-cover"
        />
      </span>
      <span className="min-w-0 flex-1">
        <span className="line-clamp-2 text-sm font-medium text-foreground">
          {highlightMatch(product.name, highlight)}
        </span>
        <span className="mt-0.5 block text-xs text-muted-foreground">
          Арт. <span className="font-mono">{highlightMatch(product.article, highlight)}</span>
          {detail && <> · {detail}</>}
        </span>
      </span>
      {trailing}
    </>
  );

  return to ? (
    <Link to={to} onClick={onClick} className={ROW_CLASS}>
      {content}
    </Link>
  ) : (
    <button type="button" onClick={onClick} className={ROW_CLASS}>
      {content}
    </button>
  );
}
